"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var type_1 = require("../types/type");
var utils_1 = require("../types/utils");
var binaryOperator_1 = require("../ast/binaryOperator");
var signatureValidator_1 = __importDefault(require("./signatureValidator"));
var evaluationErrors_1 = __importDefault(require("../evaluator/evaluationErrors"));
/**
 * A StaticAnalyzer examines a formula AST to determine the result type of the formula and produces warnings and errors
 * about the formula.
 */
var StaticAnalyzer = /** @class */ (function () {
    /**
     * Constructs a new static analyzer that uses the given context to lookup functions and variable types.
     */
    function StaticAnalyzer(context) {
        /**
         * Warnings are messages which do not interfere with the evaluation of the formula.
         */
        this.warnings = [];
        /**
         * Errors are messages which would cause an error to be thrown were the formula evaluated in its current state.
         */
        this.errors = [];
        this.context = context;
    }
    /**
     * Gets the return type of a root AST node. {@link warnings} and {@link errors} are populated by calling this method.
     *
     * This static analyzer should not be reused after {@code getType} is called.
     */
    StaticAnalyzer.prototype.getType = function (node) {
        return node.visit(this);
    };
    StaticAnalyzer.prototype.visitArrayLiteral = function (node) {
        if (node.elements.length === 0) {
            return { type: "array", elements: type_1.VoidType };
        }
        else {
            var elementOptions = node.elements.map(this.getType.bind(this));
            var elementType = utils_1.collapseUnion({ type: "union", options: elementOptions });
            if (elementType.options.length === 1) {
                elementType = elementType.options[0];
            }
            return { type: "array", elements: elementType };
        }
    };
    StaticAnalyzer.prototype.visitBinaryOperator = function (node) {
        // even if we don't use the results of checking the types of the left/right nodes,
        // we still need to call getType so the static analyzer visits that portion of the tree
        var leftType = this.getType(node.left);
        var rightType = this.getType(node.right);
        // equals operator can compare any types, others only accept numbers
        if (node.operator !== binaryOperator_1.BinaryOperator.equals) {
            if (!utils_1.isNumberType(leftType)) {
                // if the type is a valid number string, it's implicitly coerced into a number
                if (utils_1.isStringType(leftType) && leftType.value !== undefined && /\d(\.\d+)?/.test(leftType.value)) {
                    leftType = { type: "number", value: parseFloat(leftType.value) };
                }
                else {
                    this.errors.push({
                        node: node.left,
                        message: "Left-hand side of binary operator " + binaryOperator_1.getBinaryOperatorString(node.operator) + " must be a number"
                    });
                }
            }
            if (!utils_1.isNumberType(rightType)) {
                if (utils_1.isStringType(rightType) && rightType.value !== undefined && /\d+(\.\d+)?/.test(rightType.value)) {
                    rightType = { type: "number", value: parseFloat(rightType.value) };
                }
                else {
                    this.errors.push({
                        node: node.right,
                        message: "Right-hand side of binary operator " + binaryOperator_1.getBinaryOperatorString(node.operator) + " must be a number"
                    });
                }
            }
        }
        var isConstant;
        if (node.operator === binaryOperator_1.BinaryOperator.equals) {
            isConstant = function (type) { return (utils_1.isNumberType(type) && type.value !== undefined) || (utils_1.isStringType(type) && type.value !== undefined) || (utils_1.isBooleanType(type) && type.value !== undefined); };
        }
        else {
            isConstant = function (type) { return utils_1.isNumberType(type) && type.value !== undefined; };
        }
        if (isConstant(leftType) && isConstant(rightType)) {
            this.warnings.push({
                node: node,
                message: "Usage of operator " + binaryOperator_1.getBinaryOperatorString(node.operator) + " can be simplified; all inputs are constant",
            });
            var result = binaryOperator_1.evaluateBinaryOperator(leftType.value, node.operator, rightType.value);
            if (typeof result === "boolean") {
                return { type: "boolean", value: result };
            }
            else {
                return { type: "number", value: result };
            }
        }
        else if (binaryOperator_1.comparisonOperators.includes(node.operator)) {
            return type_1.BooleanType;
        }
        else {
            return type_1.NumberType;
        }
    };
    StaticAnalyzer.prototype.visitBooleanLiteral = function (node) {
        return { type: "boolean", value: node.value };
    };
    StaticAnalyzer.prototype.visitFunctionCall = function (node) {
        var _this = this;
        var _a;
        try {
            var maybeOverloadedDefinition = this.context.lookupFunction(node.name);
            var paramTypes = node.parameters.map(function (param) { return _this.getType(param); });
            var result = this.resolveOverload(node, maybeOverloadedDefinition, paramTypes);
            return (_a = result === null || result === void 0 ? void 0 : result.returnType) !== null && _a !== void 0 ? _a : type_1.AnyType;
        }
        catch (e) {
            this.errors.push({ node: node, message: "Missing function named '" + node.name + "'" });
            // return AnyType so type checking can continue
            return type_1.AnyType;
        }
    };
    StaticAnalyzer.prototype.resolveOverload = function (node, definition, parameters) {
        var _a;
        if (!Array.isArray(definition)) {
            var _b = signatureValidator_1.default.validateParameters(parameters, definition), errors = _b.errors, returnType = _b.returns;
            if (errors.length === 0) {
                return { resolvedDefinition: definition, returnType: returnType !== null && returnType !== void 0 ? returnType : type_1.AnyType };
            }
            else {
                for (var _i = 0, errors_1 = errors; _i < errors_1.length; _i++) {
                    var error = errors_1[_i];
                    var errorNode = node;
                    if (error.parameterIndex !== undefined) {
                        errorNode = node.parameters[error.parameterIndex];
                    }
                    this.errors.push({
                        node: errorNode,
                        message: error.message,
                    });
                }
                return null;
            }
        }
        var results = definition.map(function (overload) {
            var _a = signatureValidator_1.default.validateParameters(parameters, overload), errors = _a.errors, returns = _a.returns;
            return {
                resolvedOverload: overload,
                errors: errors,
                returnType: returns
            };
        });
        var validOverloads = results.filter(function (_a) {
            var errors = _a.errors;
            return errors.length === 0;
        });
        if (validOverloads.length === 1) {
            return { resolvedDefinition: validOverloads[0].resolvedOverload, returnType: (_a = validOverloads[0].returnType) !== null && _a !== void 0 ? _a : type_1.AnyType };
        }
        else if (validOverloads.length === 0) {
            this.errors.push({
                node: node,
                message: new evaluationErrors_1.default(results, false).message,
            });
            return null;
        }
        else {
            this.errors.push({
                node: node,
                message: new evaluationErrors_1.default(validOverloads, true).message,
            });
            return null;
        }
    };
    StaticAnalyzer.prototype.visitGroupNode = function (node) {
        return this.getType(node.content);
    };
    StaticAnalyzer.prototype.visitNumberLiteral = function (node) {
        return { type: "number", value: node.value };
    };
    StaticAnalyzer.prototype.visitStringLiteral = function (node) {
        return { type: "string", value: node.value };
    };
    StaticAnalyzer.prototype.visitUnaryMinus = function (node) {
        var operandType = this.getType(node.operand);
        if (!utils_1.isNumberType(operandType)) {
            this.errors.push({ node: node.operand, message: "Operand of unary minus must be a number" });
        }
        return type_1.NumberType;
    };
    StaticAnalyzer.prototype.visitVariableLookup = function (node) {
        try {
            return this.context.getVariableType(node.name);
        }
        catch (e) {
            this.errors.push({ node: node, message: "Missing variable named '" + node.name + "'" });
            // return AnyType so type checking can continue
            return type_1.AnyType;
        }
    };
    return StaticAnalyzer;
}());
exports.default = StaticAnalyzer;
//# sourceMappingURL=staticAnalyzer.js.map